import React from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { ExternalLinkIcon, MenuIcon, XIcon } from "@heroicons/react/outline";
import { getImageSrcRelOrAbsolute } from "../../utils";

function moveItem(items, from, to) {
  const moved = Array.from(items);
  const [item] = moved.splice(from, 1);
  moved.splice(to, 0, item);
  return moved;
}

function ItemRow({
  item,
  position,
  withImage,
  itemHref,
  onOpen,
  onRemove,
  removeTitle,
  dragProps = {},
  isDragging
}) {
  const href = itemHref?.(item.id);
  const openClassName = "p-1 text-blue-600 hover:text-blue-700 transition-colors";
  return (
    <li
      ref={dragProps.innerRef}
      {...dragProps.draggableProps}
      {...dragProps.dragHandleProps}
      style={dragProps.draggableProps?.style}
      className={`border rounded-md bg-white p-2 my-1 flex items-center gap-2 select-none transition-colors ${
        isDragging ? "shadow-lg border-blue-300" : "hover:bg-gray-50"
      }`}
    >
      {dragProps.dragHandleProps && (
        <div className="p-2">
          <MenuIcon className="w-5 h-5 text-gray-400" />
        </div>
      )}
      {position && (
        <span className="flex-shrink-0 w-5 text-xs font-medium text-gray-500 text-center">
          {position}
        </span>
      )}
      {withImage &&
        (item.image ? (
          <img
            src={getImageSrcRelOrAbsolute(item.image, "?tr=w-96")}
            alt=""
            className="flex-shrink-0 w-14 h-8 rounded object-cover"
          />
        ) : (
          <div className="flex-shrink-0 w-14 h-8 rounded bg-gray-200" />
        ))}
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{item.title}</p>
        {item.detail && <p className="text-sm text-gray-500">{item.detail}</p>}
      </div>
      {item.badge && (
        <span className="flex-shrink-0 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">
          {item.badge}
        </span>
      )}
      <div className="flex flex-shrink-0">
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={openClassName}
            aria-label={`Open ${item.title}`}
            title={`Open ${item.title}`}
          >
            <ExternalLinkIcon className="w-5 h-5" />
          </a>
        ) : (
          onOpen && (
            <button
              type="button"
              onClick={() => onOpen(item.id)}
              className={openClassName}
              aria-label={`Open ${item.title}`}
              title={`Open ${item.title}`}
            >
              <ExternalLinkIcon className="w-5 h-5" />
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          className="p-1 text-red-600 hover:text-red-700 transition-colors"
          aria-label={`Remove ${item.title}`}
          title={removeTitle}
        >
          <XIcon className="w-5 h-5" />
        </button>
      </div>
    </li>
  );
}

export default function LinkedItemList({
  items,
  onRemove,
  onReorder,
  itemHref,
  onOpen,
  emptyText,
  withImages = false,
  removeTitle = "Remove",
  droppableId,
  className = "max-h-96 overflow-auto"
}) {
  if (!items.length) return <p className="mt-2 text-sm text-gray-500">{emptyText}</p>;

  const rowProps = { withImage: withImages, itemHref, onOpen, onRemove, removeTitle };

  if (!onReorder) {
    return (
      <ul className={className}>
        {items.map(item => (
          <ItemRow key={item.id} item={item} {...rowProps} />
        ))}
      </ul>
    );
  }

  const onDragEnd = ({ source, destination }) => {
    if (!destination) return;
    onReorder(moveItem(items, source.index, destination.index).map(item => item.id));
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={droppableId}>
        {provided => (
          <ul {...provided.droppableProps} ref={provided.innerRef} className={className}>
            {items.map((item, index) => (
              <Draggable key={item.id} draggableId={`${droppableId}-${item.id}`} index={index}>
                {(dragProps, snapshot) => (
                  <ItemRow
                    item={item}
                    position={index + 1}
                    {...rowProps}
                    dragProps={dragProps}
                    isDragging={snapshot.isDragging}
                  />
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  );
}
